import React from 'react';
import { useAdminPanel } from './useAdminPanel';
import { ADMIN_PALETTE, ADMIN_TAB_GROUPS, AttentionBanner } from '../../components/admin/adminDesignSystem';
import { AdminPanelHeader } from '../../components/admin/AdminPanelHeader';
import { AdminPanelTabBar } from '../../components/admin/AdminPanelTabBar';
import { AdminMobileQuickActions } from '../../components/admin/AdminMobileQuickActions';
import { AdminFloatingSupportBubble } from '../../components/admin/AdminFloatingSupportBubble';
import { AdminReferralsPanel } from '../../components/admin/AdminReferralsPanel';
import { AdminMobileNav } from '../../components/admin/AdminMobileNav';
import { AdminAccountsPanel } from '../../components/admin/AdminAccountsPanel';
import { AdminAdminsPanel } from '../../components/admin/AdminAdminsPanel';
import { AdminNotificationsPanel } from '../../components/admin/AdminNotificationsPanel';
import { AdminApprovalsPanel } from '../../components/admin/AdminApprovalsPanel';
import { AdminFinancePanel } from '../../components/admin/AdminFinancePanel';
import { AdminGuestsPanel } from '../../components/admin/AdminGuestsPanel';
import { AdminListingsPanel } from '../../components/admin/AdminListingsPanel';
import { AdminGeoMapExplorer } from '../../components/admin/AdminGeoMapExplorer';
import { AdminSupportInboxPanel } from '../../components/admin/AdminSupportInboxPanel';
import { AdminFaqStatsPanel } from '../../components/admin/AdminFaqStatsPanel';
import { AdminVenueManagementPanel } from '../../components/admin/AdminVenueManagementPanel';
import { AdminVenueReviewsModal } from '../../components/admin/AdminVenueReviewsModal';
import { AdminOverviewPanel } from '../../components/admin/AdminOverviewPanel';
import { AdminDisputesPanel } from '../../components/admin/AdminDisputesPanel';
import { AdminBroadcastsPanel } from '../../components/admin/AdminBroadcastsPanel';
import { AdminAnnouncementsPanel } from '../../components/admin/AdminAnnouncementsPanel';
import { AdminVenueReportsPanel } from '../../components/admin/AdminVenueReportsPanel';
import { AdminMainAdminsPanel, ResetLaunchModal } from '../../components/admin/AdminMainAdminsPanel';
import { AdminAgreementsPanel } from '../../components/admin/AdminAgreementsPanel';
import { AdminExperiencesPanel } from '../../components/admin/AdminExperiencesPanel';
import { AdminLegalSettingsPanel } from '../../components/admin/AdminLegalSettingsPanel';
import { AdminCompliancePanel } from '../../components/admin/AdminCompliancePanel';
import { AdminEnRouteAnalyticsPanel } from '../../components/admin/AdminEnRouteAnalyticsPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPanel({ onLogout }) {
  const {
    activeTab, setActiveTab,
    applications, users, venues, ignoredIcalEvents,
    financeOverview, financeMonth, setFinanceMonth,
    referrals, venueApps,
    selectedVenueApp, setSelectedVenueApp,
    selectedGuest, setSelectedGuest,
    selectedHost, setSelectedHost,
    guestDetail, setGuestDetail,
    hostDetail, setHostDetail,
    guestDetailLoading, hostDetailLoading,
    editMode, setEditMode,
    editData, setEditData,
    loading,
    searchQuery, setSearchQuery,
    recentSearches,
    supportRequests,
    supportFilter, setSupportFilter,
    faqStats,
    selectedSupportRequestId,
    selectedSupportThread,
    supportDraft, setSupportDraft,
    supportLoading,
    pendingApprovalCount, supportOpenCount, reportsOpenCount, disputesOpenCount,
    experiencesPendingCount, setExperiencesPendingCount,
    attentionDismissed,
    expandedTimelineId, setExpandedTimelineId,
    listingStatusFilter, setListingStatusFilter,
    globalMapFilters, selectedMapId, setSelectedMapId, radiusEnabled,
    floatingSupportVisible, setFloatingSupportVisible,
    floatingSupportMinimized, setFloatingSupportMinimized,
    reviewsModalVenue, setReviewsModalVenue,
    resetModalOpen, setResetModalOpen,
    currentUser, isMobileView,
    floatingSupportBottomRef,
    headers, user, isMainAdmin,
    searchResults, pendingVenueApps, revisionVenueApps,
    globalMapItems, tabCounts, attentionItems,
    toggleMobileView, navigateToApp, handleLogout, dismissAttention,
    approveApp, rejectApp, approveVenueApplication,
    requestVenueMediaChanges, deleteUser,
    approveReferral, rejectReferral, removeReferral, markReferralInviteSent,
    generateHostPayoutBatch, markHostPayoutPaid,
    runHostPayoutSchedulerNow, runConnectRemindersNow,
    exportHostPayoutCsv, exportHostPayoutUploadCsv,
    fetchGuestDetail, fetchHostDetail,
    startAdminConversation, openSupportBubbleForUser, openTicketInMessenger,
    removeGuestQr, removeGuestReview,
    fetchSupportThread, sendSupportReply, updateSupportStatus,
    saveRecentSearch, deleteListingRecord,
    openGeneralMapForItem, openMapItemDetail,
    venueAction, saveVenueEdit, movePhoto,
    handleFilterToggle, toggleRadius, onViewReviews,
  } = useAdminPanel({ onLogout });

  return (
    <div
      className="admin-scope"
      style={{
        maxWidth: isMobileView ? '100%' : 1320,
        margin: '0 auto',
        minHeight: '100dvh',
        background: ADMIN_PALETTE.surfaceMuted,
        fontFamily: "'Manrope', sans-serif",
        paddingTop: isMobileView ? 'max(28px, env(safe-area-inset-top, 0px))' : 16,
        paddingRight: isMobileView ? 'max(14px, env(safe-area-inset-right, 0px))' : 16,
        paddingBottom: 0,
        paddingLeft: isMobileView ? 'max(14px, env(safe-area-inset-left, 0px))' : 16,
        overflowX: isMobileView ? 'hidden' : undefined,
        boxSizing: 'border-box',
      }}
    >
      <style>{`
        @keyframes admin-support-bubble-in {
          from { opacity: 0; transform: translateY(10px) scale(0.96); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes adminMobileDrawerIn {
          from { transform: translateY(24px); opacity: 0; }
          to   { transform: translateY(0); opacity: 1; }
        }
        .admin-map-popup .maplibregl-popup-content {
          padding: 12px 14px; border-radius: 14px;
          box-shadow: 0 12px 28px rgba(15,17,21,0.18);
          border: 1px solid rgba(15,17,21,0.08);
        }
        .admin-map-popup .maplibregl-popup-tip { display: none; }
      `}</style>

      {/* Header */}
      <AdminPanelHeader
        isMobileView={isMobileView}
        isMainAdmin={isMainAdmin}
        activeTab={activeTab}
        onMapsClick={() => setActiveTab('maps')}
        toggleMobileView={toggleMobileView}
        handleLogout={handleLogout}
        onResetClick={() => setResetModalOpen(true)}
        onAppViewClick={navigateToApp}
        onSelectTab={setActiveTab}
      />

      {/* Desktop tab nav */}
      {!isMobileView && (
        <AdminPanelTabBar
          activeTab={activeTab}
          onTabClick={setActiveTab}
          tabCounts={tabCounts}
          isMainAdmin={isMainAdmin}
        />
      )}

      {/* Needs-attention banner */}
      {!attentionDismissed && (
        <AttentionBanner items={attentionItems} onDismiss={dismissAttention} />
      )}

      {/* ── Content ─────────────────────────────────────────────────────── */}
      <div style={{ padding: isMobileView ? '0 0 calc(80px + env(safe-area-inset-bottom, 0px))' : 4 }}>

        {/* Mobile: active tab breadcrumb */}
        {isMobileView && (() => {
          const allTabs = ADMIN_TAB_GROUPS.flatMap((g) => g.tabs);
          const currentTab = allTabs.find((t) => t.id === activeTab);
          if (!currentTab) return null;
          const Icon = currentTab.icon;
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 4px 12px' }}>
              <Icon size={16} color={ADMIN_PALETTE.inkMuted} />
              <span style={{
                fontSize: 13, fontWeight: 700, color: ADMIN_PALETTE.inkMuted,
                textTransform: 'uppercase', letterSpacing: 0.6,
                fontFamily: "'Manrope', sans-serif",
              }}>
                {currentTab.label}
              </span>
            </div>
          );
        })()}

        {loading && <p style={{ textAlign: 'center', color: '#999', padding: 20 }}>Loading...</p>}

        {/* Dashboard */}
        {activeTab === 'dashboard' && (
          <>
            {isMobileView && (
              <AdminMobileQuickActions
                pendingApprovalCount={pendingApprovalCount}
                supportOpenCount={supportOpenCount}
                reportsOpenCount={reportsOpenCount}
                disputesOpenCount={disputesOpenCount}
                onNavigateTab={setActiveTab}
              />
            )}
            <AdminOverviewPanel
              headers={headers}
              onNavigateTab={setActiveTab}
              onSelectVenue={(venue) => {
                if (!venue?.id) return;
                setActiveTab('venue-apps');
                const match = venueApps.find((v) => v.id === venue.id);
                if (match) setSelectedVenueApp(match);
              }}
              onSelectHost={(host) => {
                if (!host?.id) return;
                fetchHostDetail(host.id);
                setActiveTab('applications');
              }}
              onSelectGuest={(guest) => {
                if (!guest?.id) return;
                fetchGuestDetail(guest.id);
                setActiveTab('guests');
              }}
            />
          </>
        )}

        {/* Approving */}
        {activeTab === 'approving' && (
          <AdminApprovalsPanel
            approveVenueApplication={approveVenueApplication}
            listingApplications={applications}
            mobileMode={isMobileView}
            onContactUser={openSupportBubbleForUser}
            onApproveReferral={approveReferral}
            onRejectReferral={rejectReferral}
            onMarkReferralInviteSent={markReferralInviteSent}
            onRemoveReferral={removeReferral}
            onRequestMediaChanges={requestVenueMediaChanges}
            onStartConversation={startAdminConversation}
            onViewOnMap={openGeneralMapForItem}
            pendingApprovalCount={pendingApprovalCount}
            pendingVenueApps={pendingVenueApps}
            revisionVenueApps={revisionVenueApps}
            referredVenueItems={referrals.filter((e) => e.referral_type === 'venue' && ['pending', 'approved'].includes(e.status))}
            selectedVenueApp={selectedVenueApp}
            setSelectedVenueApp={setSelectedVenueApp}
            backendUrl={BACKEND_URL}
            venues={venues}
            venueAction={venueAction}
          />
        )}

        {/* Host Listings */}
        {activeTab === 'applications' && (
          <AdminListingsPanel
            applications={applications}
            ignoredIcalEvents={ignoredIcalEvents}
            onApproveListing={approveApp}
            onDeleteListing={deleteListingRecord}
            expandedTimelineId={expandedTimelineId}
            fetchHostDetail={fetchHostDetail}
            hostDetail={hostDetail}
            hostDetailLoading={hostDetailLoading}
            listingStatusFilter={listingStatusFilter}
            mobileMode={isMobileView}
            onContactUser={openSupportBubbleForUser}
            onRejectListing={rejectApp}
            onStartConversation={startAdminConversation}
            onViewOnMap={openGeneralMapForItem}
            searchQuery={searchQuery}
            selectedHost={selectedHost}
            setExpandedTimelineId={setExpandedTimelineId}
            setHostDetail={setHostDetail}
            setListingStatusFilter={setListingStatusFilter}
            setSearchQuery={setSearchQuery}
            setSelectedHost={setSelectedHost}
          />
        )}

        {/* Global Map */}
        {activeTab === 'maps' && (
          <AdminGeoMapExplorer
            dataTestId="admin-global-map-explorer"
            description="Click any pin to jump to its card on the left, then hit 'Open full details' to verify and act in two clicks."
            enableHoverRadius
            emptyCopy="No map items available for this filter yet."
            filterOptions={[
              { id: 'all',             label: 'All' },
              { id: 'active-listings', label: 'Active listings' },
              { id: 'early-listings',  label: 'Early listings' },
              { id: 'active-venues',   label: 'Active venues' },
              { id: 'pending-venues',  label: 'Pending venues' },
            ]}
            items={globalMapItems}
            onFilterToggle={handleFilterToggle}
            onOpenDetail={openMapItemDetail}
            onSelect={setSelectedMapId}
            onToggleRadius={toggleRadius}
            radiusEnabled={radiusEnabled}
            selectedId={selectedMapId}
            selectedFilters={globalMapFilters}
            title="Global listings & venues map"
          />
        )}

        {/* Users */}
        {activeTab === 'users' && (
          <AdminAccountsPanel
            guestDetail={guestDetail}
            guestDetailLoading={guestDetailLoading}
            hostDetail={hostDetail}
            hostDetailLoading={hostDetailLoading}
            recentSearches={recentSearches}
            searchQuery={searchQuery}
            searchResults={searchResults}
            selectedGuest={selectedGuest}
            selectedHost={selectedHost}
            setActiveTab={setActiveTab}
            setGuestDetail={setGuestDetail}
            setHostDetail={setHostDetail}
            setSearchQuery={setSearchQuery}
            setSelectedGuest={setSelectedGuest}
            setSelectedHost={setSelectedHost}
            setSelectedVenueApp={setSelectedVenueApp}
            users={users}
            venueApps={venueApps}
            expandedTimelineId={expandedTimelineId}
            setExpandedTimelineId={setExpandedTimelineId}
            onDeleteReview={removeGuestReview}
            onDeleteUser={deleteUser}
            onContactUser={openSupportBubbleForUser}
            onFetchGuestDetail={fetchGuestDetail}
            onFetchHostDetail={fetchHostDetail}
            onRemoveQr={removeGuestQr}
            onSaveRecentSearch={saveRecentSearch}
            onStartConversation={startAdminConversation}
          />
        )}

        {/* Guests */}
        {activeTab === 'guests' && (
          <AdminGuestsPanel
            expandedTimelineId={expandedTimelineId}
            guestDetail={guestDetail}
            guestDetailLoading={guestDetailLoading}
            onContactUser={openSupportBubbleForUser}
            onDeleteReview={removeGuestReview}
            onDeleteUser={deleteUser}
            onFetchGuestDetail={fetchGuestDetail}
            onRemoveQr={removeGuestQr}
            onStartConversation={startAdminConversation}
            searchQuery={searchQuery}
            selectedGuest={selectedGuest}
            setExpandedTimelineId={setExpandedTimelineId}
            setGuestDetail={setGuestDetail}
            setSearchQuery={setSearchQuery}
            setSelectedGuest={setSelectedGuest}
            users={users}
          />
        )}

        {/* Finance */}
        {activeTab === 'transactions' && (
          <AdminFinancePanel
            financeOverview={financeOverview}
            loading={loading}
            headers={headers}
            onExportCsv={exportHostPayoutCsv}
            onExportUploadCsv={exportHostPayoutUploadCsv}
            onGenerateBatch={generateHostPayoutBatch}
            onMarkHostPaid={markHostPayoutPaid}
            onRunSchedulerNow={runHostPayoutSchedulerNow}
            onRunConnectReminders={runConnectRemindersNow}
            selectedMonth={financeMonth}
            setSelectedMonth={setFinanceMonth}
          />
        )}

        {/* Referrals */}
        {activeTab === 'referrals' && (
          <AdminReferralsPanel referrals={referrals} onApprove={approveReferral} />
        )}

        {/* Support Inbox */}
        {activeTab === 'support' && (
          <AdminSupportInboxPanel
            fetchSupportThread={fetchSupportThread}
            selectedSupportRequestId={selectedSupportRequestId}
            selectedSupportThread={selectedSupportThread}
            sendSupportReply={sendSupportReply}
            setSupportDraft={setSupportDraft}
            setSupportFilter={setSupportFilter}
            supportDraft={supportDraft}
            supportFilter={supportFilter}
            supportLoading={supportLoading}
            supportRequests={supportRequests}
            updateSupportStatus={updateSupportStatus}
            openTicketInMessenger={openTicketInMessenger}
            user={user}
            mobileMode={isMobileView}
          />
        )}

        {activeTab === 'reports' && (
          <AdminVenueReportsPanel
            headers={headers}
            onMessageReporter={openSupportBubbleForUser}
          />
        )}

        {activeTab === 'disputes' && <AdminDisputesPanel headers={headers} />}

        {activeTab === 'broadcasts' && <AdminBroadcastsPanel headers={headers} />}

        {activeTab === 'announcements' && <AdminAnnouncementsPanel headers={headers} />}

        {/* Feedback */}
        {activeTab === 'feedback' && (
          <AdminSupportInboxPanel
            fetchSupportThread={fetchSupportThread}
            selectedSupportRequestId={selectedSupportRequestId}
            selectedSupportThread={selectedSupportThread}
            sendSupportReply={sendSupportReply}
            setSupportDraft={setSupportDraft}
            setSupportFilter={setSupportFilter}
            supportDraft={supportDraft}
            supportFilter={supportFilter}
            supportLoading={supportLoading}
            supportRequests={supportRequests}
            updateSupportStatus={updateSupportStatus}
            openTicketInMessenger={openTicketInMessenger}
            user={user}
            reasonFilter="Feedback"
            headerTitle="User Feedback"
            headerSubtitle="Ideas, bug reports and praise from users across every role. Mark Solved once you've acted on or acknowledged the feedback."
            mobileMode={isMobileView}
          />
        )}

        {activeTab === 'faq-stats' && (
          <AdminFaqStatsPanel faqStats={faqStats} loading={loading} />
        )}

        {activeTab === 'en-route' && (
          <AdminEnRouteAnalyticsPanel headers={headers} />
        )}

        {activeTab === 'admins' && (
          <AdminAdminsPanel headers={headers} currentUser={currentUser} />
        )}

        {activeTab === 'notifications' && (
          <AdminNotificationsPanel headers={headers} />
        )}

        {activeTab === 'main-admins' && isMainAdmin && (
          <AdminMainAdminsPanel headers={headers} currentUser={currentUser} />
        )}

        {activeTab === 'agreements' && <AdminAgreementsPanel headers={headers} />}

        {activeTab === 'experiences' && (
          <AdminExperiencesPanel
            isMobileView={isMobileView}
            onCountsChange={({ pending }) => {
              if (typeof setExperiencesPendingCount === 'function') {
                setExperiencesPendingCount(pending);
              }
            }}
          />
        )}

        {activeTab === 'legal-settings' && <AdminLegalSettingsPanel headers={headers} />}

        {activeTab === 'compliance' && <AdminCompliancePanel headers={headers} currentUser={currentUser} />}

        {/* Venue Management */}
        {activeTab === 'venue-apps' && (
          <AdminVenueManagementPanel
            backendUrl={BACKEND_URL}
            editData={editData}
            editMode={editMode}
            expandedTimelineId={expandedTimelineId}
            movePhoto={movePhoto}
            mobileMode={isMobileView}
            onContactUser={openSupportBubbleForUser}
            onSaveVenueEdit={saveVenueEdit}
            searchQuery={searchQuery}
            selectedVenueApp={selectedVenueApp}
            setEditData={setEditData}
            setEditMode={setEditMode}
            setSearchQuery={setSearchQuery}
            setSelectedVenueApp={setSelectedVenueApp}
            setExpandedTimelineId={setExpandedTimelineId}
            startAdminConversation={startAdminConversation}
            onViewOnMap={openGeneralMapForItem}
            onViewReviews={onViewReviews}
            venueAction={venueAction}
            venueApps={venueApps}
            venues={venues}
          />
        )}
      </div>

      {/* ── Modals ────────────────────────────────────────────────────────── */}
      {reviewsModalVenue && (
        <AdminVenueReviewsModal
          venueId={reviewsModalVenue.id}
          venueName={reviewsModalVenue.name}
          headers={headers}
          onClose={() => setReviewsModalVenue(null)}
        />
      )}

      {resetModalOpen && (
        <ResetLaunchModal
          headers={headers}
          currentUser={currentUser}
          onClose={() => setResetModalOpen(false)}
        />
      )}

      {/* ── Floating support bubble ──────────────────────────────────────── */}
      {floatingSupportVisible && selectedSupportThread && activeTab !== 'support' && (
        <AdminFloatingSupportBubble
          isMobileView={isMobileView}
          floatingSupportMinimized={floatingSupportMinimized}
          setFloatingSupportMinimized={setFloatingSupportMinimized}
          setFloatingSupportVisible={setFloatingSupportVisible}
          selectedSupportThread={selectedSupportThread}
          userId={user?.id}
          updateSupportStatus={updateSupportStatus}
          supportDraft={supportDraft}
          setSupportDraft={setSupportDraft}
          sendSupportReply={sendSupportReply}
          scrollAnchorRef={floatingSupportBottomRef}
        />
      )}

      {/* ── Mobile bottom nav ────────────────────────────────────────────── */}
      {isMobileView && (
        <AdminMobileNav
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          tabCounts={tabCounts}
          isMainAdmin={isMainAdmin}
          onLogout={handleLogout}
          onViewApp={navigateToApp}
          onViewMaps={() => setActiveTab('maps')}
        />
      )}
    </div>
  );
}
